import { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { reportBypassedHttpError } from '@/errors';
import { SurgeonDashboardData, type ISurgeonDashboard } from '@/models/surgeonDashboard';
import type { FormField } from '@/models/form';
import type { PerfusionSectionKind } from '@/models/perfusionSection';
import { BACKEND_URL, signedApiClient, signedHeaders } from './apiClient';
import { devDelay } from '@/utils/devDelay';
import { ndjsonLines } from '@/utils/ndjson';
import { PerfusionQuery } from './perfusionQuery';

/** The sections the combined dashboard route sends, in the order it sends them. */
export type PerfusionDashboardSection = 'summary' | PerfusionSectionKind;

/** One line of the combined route as the wire carries it, before the summary is formatted. */
interface PerfusionDashboardWireChunk {
  section: PerfusionDashboardSection;
  /** Absent when the server could not build this section. */
  data?: Record<string, unknown>;
  error?: string;
}

/**
 * One line of the combined route, ready to publish.
 *
 * The summary arrives under `summary` and a section's rows under `data`, rather than both under one
 * key the caller reads two ways: `section` cannot discriminate them — `PerfusionSectionKind` is
 * `string`, so `'summary'` is one of its values — and the cast that stood in for narrowing is what
 * let this route publish an unmapped summary in the first place. Exactly one of the two is set,
 * and neither is when `error` is.
 */
export interface PerfusionDashboardChunk {
  section: PerfusionDashboardSection;
  /** The summary, formatted by `SurgeonDashboardData.mapFromBackend`. */
  summary?: SurgeonDashboardData;
  /** One section's rows, under the section's own key. */
  data?: Record<string, unknown>;
  error?: string;
}

/**
 * Reads of the interactive endpoint, cached and cancellable.
 *
 * One instance for the app: the cache is keyed on the case, so sharing it
 * across the dashboard's graphs is what lets a variable the surgeon already
 * looked at come back without a round trip. `invalidate` scopes by key prefix,
 * and every key starts with the case id.
 */
const interactiveQuery = new PerfusionQuery();


/** A JSON error body as an object, or the raw text when the server did not send JSON. */
const parsedBody = (body: string): unknown => {
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
};

/** Response headers as a plain object, tolerant of a test double with no real `Headers`. */
const headersOf = (response: Response): Record<string, string> => {
  const headers: Record<string, string> = {};
  response.headers?.forEach?.((value, name) => {
    headers[name] = value;
  });
  return headers;
};

/**
 * A failed streamed response, reported through the error layer and returned ready to throw.
 *
 * `streamPerfusionDashboard` uses `fetch` because axios buffers the body it needs to read as it
 * arrives, and the cost of leaving axios is that `installHttpErrorInterceptor` never sees the
 * request. An expired session on this route therefore reached nobody: no logout, no trail entry,
 * and the dashboard sat there until the per-section fallback happened to 401 through the axios
 * path and triggered it instead. Relying on the fallback for that is what this removes — the
 * fallback is a resilience feature, not the app's auth handling.
 *
 * A real `AxiosError` rather than a lookalike: `normalizeError` reads status, method, path and
 * body off exactly that shape — `isAuthExpiredError` matches /expired/ against `response.data`, so
 * the body has to be read here — and ~20 call sites test `instanceof AxiosError`. Built this way
 * the stream classifies identically to the axios path instead of through a second code path that
 * can drift from it.
 */
const streamHttpError = async (response: Response, uri: string): Promise<AxiosError> => {
  // The body is an error payload and small. `.catch` because a body already consumed or cut off
  // mid-flight must still produce the status-based classification rather than a second failure.
  const body = await response.text().catch(() => '');
  const config = { url: uri, method: 'get' } as InternalAxiosRequestConfig;
  const error = new AxiosError(
    `perfusion-dashboard responded ${response.status} ${response.statusText}`,
    response.status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
    config,
    undefined,
    {
      status: response.status,
      statusText: response.statusText,
      data: parsedBody(body),
      headers: headersOf(response),
      config
    } as AxiosResponse
  );
  reportBypassedHttpError(error);
  return error;
};

class SurgeonDashboardService {

  /**
   * Every read below starts with `await devDelay()`.
   *
   * The dashboard is most of the app's loading states — a summary skeleton, four section skeletons,
   * the `reloading` dim on a refresh, four progress lines and a live-region announcement per
   * section — and against a local API none of them stay on screen long enough to look at. The helper
   * is inert unless `VITE_APP_FAKE_API_DELAY_MS` is set, and compiles out of a production build
   * entirely; see `src/utils/devDelay.ts`.
   */

  initService() {}

  async getPerfusionSummaryData(caseId: string) {
    await devDelay();
    const uri = `pump/case/${caseId}/perfusion-data/summary`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    return SurgeonDashboardData.mapFromBackend(response.data);
  }

  /**
   * One perfusion section's rows.
   *
   * Was four identical methods differing only in the segment. `kind` is both the URL segment and the
   * key the response answers under, which is the convention that makes a fifth section one line in
   * `PERFUSION_SECTIONS` rather than a method here, an action in the store and a block in the template.
   *
   * `SurgeonDashboardData.mapFromBackend` is deliberately not used: it transforms
   * `perfusionSummaryData` and passes the section arrays through untouched, and it is typed to four
   * fixed keys, which is exactly the thing being removed.
   */
  async getPerfusionSectionData(kind: PerfusionSectionKind, caseId: string): Promise<FormField[]> {
    await devDelay();
    const uri = `pump/case/${caseId}/perfusion-data/${kind}`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    return (response.data?.[kind] as FormField[]) ?? [];
  }

  /**
   * The whole dashboard from one request, a section at a time.
   *
   * `fetch` rather than the axios client, because the response is read as it
   * streams and axios buffers it whole. `signedHeaders` is exported for exactly
   * this: the signature covers the request line, so it is the same credential
   * the axios path would have sent.
   *
   * The cost of leaving axios is that `installHttpErrorInterceptor` does not
   * see this request. `streamHttpError` closes that gap: a failed response is
   * reported through the same seam the interceptor uses, so a 401 here logs the
   * user out rather than waiting for the per-section fallback to 401 as well.
   * The fallback is resilience now, not the auth path.
   *
   * A failure still throws rather than being absorbed, which is what makes this
   * route safe to add — an API that does not serve it yet answers 404 and the
   * client carries on the old way.
   */
  async *streamPerfusionDashboard(
    caseId: string,
    signal?: AbortSignal
  ): AsyncGenerator<PerfusionDashboardChunk> {
    await devDelay();
    const uri = `pump/case/${caseId}/perfusion-dashboard`;
    const response = await fetch(`${BACKEND_URL}/${uri}`, {
      method: 'GET',
      headers: {
        Accept: 'application/x-ndjson',
        ...signedHeaders('GET', uri)
      },
      signal
    });

    if (!response.ok) {
      throw await streamHttpError(response, uri);
    }
    if (!response.body) {
      throw new Error('perfusion-dashboard responded without a body');
    }

    for await (const chunk of ndjsonLines<PerfusionDashboardWireChunk>(response.body)) {
      if (chunk.section !== 'summary') {
        yield chunk;
        continue;
      }
      // The same mapping `getPerfusionSummaryData` applies, and for the same reason: the summary's
      // columns hold epoch seconds for a date-time and `{"value":770,"unit":"g"}` for a weight.
      // This route published them raw, so a cross-clamp time read `1749584280`, a liver weight read
      // as its own JSON, and every variable the case never recorded drew a labelled empty tile.
      yield {
        section: 'summary',
        error: chunk.error,
        summary: chunk.data ?
          SurgeonDashboardData.mapFromBackend(chunk.data as unknown as ISurgeonDashboard) :
          undefined
      };
    }
  }

  /**
   * Drop cached interactive answers for one case, or for every case.
   *
   * The prefix has to be the start of the key, and the keys are request lines,
   * so it is the URI prefix rather than the bare case id.
   */
  invalidateInteractiveData(caseId?: string) {
    interactiveQuery.invalidate(
      caseId ? `pump/case/${caseId}/` : undefined
    );
  }

  async getInteractiveSurgeonDashboardData(caseId: string, variable?: string, postFilters?: Record<string, string>) {
    await devDelay();
    let uri = `pump/case/${caseId}/interactive-perfusion-data`;
    if (variable) {
      uri += `?variable=${variable}`;
      Object.keys(postFilters ?? {}).forEach(key => {
        uri += `&${key}=${encodeURIComponent(postFilters![key])}`;
      });
    } else if (postFilters) {
      uri += '?';
      Object.keys(postFilters).forEach(key => {
        uri += `${key}=${encodeURIComponent(postFilters[key])}&`;
      });
      uri = uri.slice(0, -1);
    }
    // Cached, deduplicated and cancellable. The key is the whole request line,
    // which already carries the case, the variable, the filters and the
    // adjustment — the four things that change the answer. Grouping per case
    // means switching variable aborts the variable being left behind instead of
    // racing it.
    return await interactiveQuery.run({
      key: uri,
      group: caseId,
      load: async signal => {
        const apiClient = signedApiClient('GET', uri);
        const response = await apiClient.get(uri, { signal });
        if (variable) {
          return response.data;
        }
        return SurgeonDashboardData.mapFromBackend(response.data);
      }
    });
  }

  async getPumpQcReportsToken(studyId: string) {
    await devDelay();
    const uri = `pump/study/${studyId}/qc-reports-token`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    return response.data as {
      token: string;
      role: string;
      allowed_centers: string[];
    };
  }

}

export default new SurgeonDashboardService();
